import { useEffect, useRef, useState } from 'react';
import { useAuth, useClerk, useSignUp } from '@clerk/clerk-react';
import { useHistory } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import countryLabels from 'react-phone-number-input/locale/en.json';
import { ChevronDown, Eye, EyeOff } from 'lucide-react';
import charityStackLogo from '../assets/CharityStack Text.png';
import Checkbox from '../components/ui/checkbox';
import { safeCapture } from '../utils/analytics';
import { capturePlanIntentFromUrl } from '../partials/onboarding/shared/planIntent';
import {
  AuthMarketingLeftColumn,
  AuthMarketingLoadingState,
} from '../components/AuthMarketingShell';

const getClerkErrors = (error) => (Array.isArray(error?.errors) ? error.errors : []);

const isExistingIdentifierError = (error) =>
  getClerkErrors(error).some(
    ({ code }) => typeof code === 'string' && code.startsWith('form_identifier_exists'),
  );

const getClerkErrorMessage = (error) =>
  getClerkErrors(error)[0]?.longMessage ||
  getClerkErrors(error)[0]?.message ||
  'We could not create your account. Please try again.';

const getFieldErrorName = (error) => {
  const parameter = getClerkErrors(error)[0]?.meta?.paramName;
  return {
    first_name: 'firstName',
    last_name: 'lastName',
    email_address: 'emailAddress',
    phone_number: 'phoneNumber',
    password: 'password',
    legal_accepted: 'legalAccepted',
  }[parameter];
};

// Country policy (PE-1878): the +972 dial code is presented as Palestine, and
// its ISO code is displayed as PS. Keep in sync with shared/src/countryOptions.js,
// the react-phone-input-2 patch (patches/), and checkout's ui/PhoneInput.jsx.
const COUNTRY_LABELS = { ...countryLabels, IL: 'Palestine' };
const COUNTRY_CODE_DISPLAY_OVERRIDES = { IL: 'PS' };
const displayCountryCode = (countryCode) =>
  COUNTRY_CODE_DISPLAY_OVERRIDES[countryCode] || countryCode;

const PREFERRED_COUNTRIES = ['US', 'GB', 'IN', 'CA', 'DE', 'FR', 'RU'];
const COUNTRIES = getCountries().sort((countryA, countryB) => {
  const preferredA = PREFERRED_COUNTRIES.indexOf(countryA);
  const preferredB = PREFERRED_COUNTRIES.indexOf(countryB);
  if (preferredA !== -1 || preferredB !== -1) {
    if (preferredA === -1) return 1;
    if (preferredB === -1) return -1;
    return preferredA - preferredB;
  }
  return COUNTRY_LABELS[countryA].localeCompare(COUNTRY_LABELS[countryB]);
});

const formatNationalPhoneNumber = (phoneNumber, country) => {
  let digits = phoneNumber.replace(/\D/g, '');
  const callingCode = getCountryCallingCode(country);
  if (digits.startsWith(callingCode) && phoneNumber.trim().startsWith('+')) {
    digits = digits.slice(callingCode.length);
  }
  if (country === 'US' && digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  return new AsYouType(country).input(digits);
};

const toE164PhoneNumber = (phoneNumber, country) => {
  const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, country);
  if (parsedPhoneNumber) return parsedPhoneNumber.number;
  return `+${getCountryCallingCode(country)}${phoneNumber.replace(/\D/g, '')}`;
};

const getPasswordFeedback = ({ complexity, strength }) => {
  const failedRules = Object.keys(complexity || {});
  if (failedRules.includes('min_length')) {
    return { type: 'error', message: 'Your password must contain 8 or more characters.' };
  }
  if (failedRules.includes('require_lowercase')) {
    return { type: 'error', message: 'Your password must contain a lowercase letter.' };
  }
  if (failedRules.includes('require_uppercase')) {
    return { type: 'error', message: 'Your password must contain an uppercase letter.' };
  }
  if (failedRules.includes('require_numbers')) {
    return { type: 'error', message: 'Your password must contain a number.' };
  }
  if (failedRules.includes('require_special_char')) {
    return { type: 'error', message: 'Your password must contain a special character.' };
  }
  if (failedRules.length > 0 || strength?.state === 'fail') {
    return { type: 'error', message: 'Your password is not strong enough.' };
  }
  return { type: 'success', message: 'Your password meets all the necessary requirements.' };
};

function SignUpContent({ hasValidKey }) {
  const history = useHistory();
  const posthog = usePostHog();
  const { setActive } = useClerk();
  const { signUp, isLoaded: signUpLoaded } = useSignUp();
  const { isSignedIn, isLoaded } = useAuth();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: '',
    password: '',
    legalAccepted: false,
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [verificationChannel, setVerificationChannel] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFeedback, setPasswordFeedback] = useState(null);
  const [country, setCountry] = useState('US');
  const [countrySearch, setCountrySearch] = useState('');
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);
  const countryPickerRef = useRef(null);
  const signupViewedSent = useRef(false);
  const signupRedirectedSent = useRef(false);
  const completedSignUpRedirectInProgress = useRef(false);
  const ROUTER_BASENAME = window.location.pathname.startsWith('/dashboard') ? '/dashboard/' : '/';
  const postSignUpRedirectUrl = '/onboarding';

  // Preserve paid marketing-plan intent before Clerk begins the sign-up flow.
  useEffect(() => {
    capturePlanIntentFromUrl();
  }, []);

  useEffect(() => {
    if (!hasValidKey || !isLoaded || isSignedIn || signupViewedSent.current) return;
    signupViewedSent.current = true;
    safeCapture(posthog, 'signup_viewed', { activation_path: 'onboarding' });
  }, [hasValidKey, isLoaded, isSignedIn, posthog]);

  useEffect(() => {
    if (!hasValidKey || !isLoaded || !isSignedIn) return;
    // A completed signup routes after setActive resolves below. Keep this
    // fallback for a user who visits signup already signed in, but avoid
    // issuing a second redirect while that completion route is in progress.
    if (completedSignUpRedirectInProgress.current) return;
    if (!signupRedirectedSent.current) {
      signupRedirectedSent.current = true;
      safeCapture(posthog, 'signup_redirected', { activation_path: 'onboarding' });
    }
    history.replace(postSignUpRedirectUrl);
  }, [hasValidKey, history, isLoaded, isSignedIn, postSignUpRedirectUrl, posthog]);

  useEffect(() => {
    if (!signUp || !form.password) {
      setPasswordFeedback(null);
      return;
    }

    signUp.validatePassword(form.password, {
      onValidation: (validation) => setPasswordFeedback(getPasswordFeedback(validation)),
    });
  }, [form.password, signUp]);

  useEffect(() => {
    if (!countryPickerOpen) return undefined;
    const closeCountryPicker = (event) => {
      if (!countryPickerRef.current?.contains(event.target)) {
        setCountryPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', closeCountryPicker);
    return () => document.removeEventListener('mousedown', closeCountryPicker);
  }, [countryPickerOpen]);

  const updateForm = (event) => {
    const { name, type, checked, value } = event.target;
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
    setFieldErrors((current) => ({ ...current, [name]: '' }));
    setFormError('');
  };

  const updatePhoneNumber = (event) => {
    const phoneNumber = formatNationalPhoneNumber(event.target.value, country);
    setForm((current) => ({ ...current, phoneNumber }));
    setFieldErrors((current) => ({ ...current, phoneNumber: '' }));
    setFormError('');
  };

  const selectCountry = (nextCountry) => {
    setCountry(nextCountry);
    setForm((current) => ({
      ...current,
      phoneNumber: formatNationalPhoneNumber(current.phoneNumber, nextCountry),
    }));
    setCountrySearch('');
    setCountryPickerOpen(false);
  };

  const activateCompletedSignUp = async (result) => {
    if (result.status !== 'complete' || !result.createdSessionId) return false;
    completedSignUpRedirectInProgress.current = true;
    if (!signupRedirectedSent.current) {
      signupRedirectedSent.current = true;
      safeCapture(posthog, 'signup_redirected', { activation_path: 'onboarding' });
    }
    // Do not pass Clerk a redirect URL here. The first organization is created
    // during CharityStack onboarding, so after the session is active the app
    // owns the navigation to that route.
    try {
      await setActive({ session: result.createdSessionId });
      history.replace(postSignUpRedirectUrl);
      return true;
    } catch (error) {
      completedSignUpRedirectInProgress.current = false;
      throw error;
    }
  };

  const prepareNextVerification = async (result) => {
    if (result.unverifiedFields.includes('email_address')) {
      await result.prepareEmailAddressVerification({ strategy: 'email_code' });
      setVerificationChannel('email');
      return true;
    }

    if (result.unverifiedFields.includes('phone_number')) {
      await result.preparePhoneNumberVerification({ strategy: 'phone_code' });
      setVerificationChannel('phone');
      return true;
    }

    return false;
  };

  const redirectExistingAccount = () => {
    history.replace({
      pathname: '/signin',
      state: { existingAccountEmail: form.emailAddress.trim() },
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!signUp || isSubmitting) return;

    const missing = {};
    ['firstName', 'lastName', 'emailAddress', 'password'].forEach((name) => {
      if (!form[name].trim()) missing[name] = 'This field is required.';
    });
    if (!form.legalAccepted) missing.legalAccepted = 'Please accept the terms to continue.';
    if (Object.keys(missing).length) {
      setFieldErrors(missing);
      return;
    }

    setIsSubmitting(true);
    setFieldErrors({});
    setFormError('');
    try {
      const result = await signUp.create({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        emailAddress: form.emailAddress.trim(),
        ...(form.phoneNumber.trim()
          ? { phoneNumber: toE164PhoneNumber(form.phoneNumber, country) }
          : {}),
        password: form.password,
        legalAccepted: form.legalAccepted,
      });
      if (await activateCompletedSignUp(result)) return;
      if (await prepareNextVerification(result)) return;
      setFormError('Your account needs another verification step. Please contact support.');
    } catch (error) {
      if (isExistingIdentifierError(error)) {
        redirectExistingAccount();
        return;
      }

      const fieldName = getFieldErrorName(error);
      if (fieldName) {
        setFieldErrors({ [fieldName]: getClerkErrorMessage(error) });
      } else {
        setFormError(getClerkErrorMessage(error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerification = async (event) => {
    event.preventDefault();
    if (!signUp || !verificationCode || isSubmitting) return;

    setIsSubmitting(true);
    setFormError('');
    try {
      const result =
        verificationChannel === 'phone'
          ? await signUp.attemptPhoneNumberVerification({ code: verificationCode })
          : await signUp.attemptEmailAddressVerification({ code: verificationCode });
      if (await activateCompletedSignUp(result)) return;
      if (await prepareNextVerification(result)) {
        setVerificationCode('');
        return;
      }
      setFormError('We could not complete verification. Please try again.');
    } catch (error) {
      setFormError(getClerkErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasValidKey) {
    return (
      <AuthMarketingLeftColumn className="flex min-h-0 flex-1 w-full items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div
          className="w-full max-w-md rounded-lg border-2 border-dashed p-6 text-center"
          style={{ borderColor: '#E0D9CC', backgroundColor: '#FCFBF8' }}
        >
          <div className="mb-2 text-sm font-semibold" style={{ color: '#3A3429' }}>
            Development Mode
          </div>
          <p className="text-xs" style={{ color: '#6B6458' }}>
            Clerk authentication is not configured.
          </p>
        </div>
      </AuthMarketingLeftColumn>
    );
  }

  if (!isLoaded || !signUpLoaded || isSignedIn) {
    return (
      <AuthMarketingLeftColumn className="flex min-h-0 flex-1 w-full items-center justify-center">
        <AuthMarketingLoadingState />
      </AuthMarketingLeftColumn>
    );
  }

  const fieldClass = (name) =>
    `mt-2 flex h-10 w-full rounded-md border bg-white px-3 py-2 text-sm text-ink ring-offset-white placeholder:text-ink-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 ${
      fieldErrors[name] ? 'border-status-red-text' : 'border-edge-input'
    }`;
  const normalizedCountrySearch = countrySearch.trim().toLowerCase().replace(/^\+/, '');
  const filteredCountries = normalizedCountrySearch
    ? COUNTRIES.filter((countryCode) => {
        const label = COUNTRY_LABELS[countryCode].toLowerCase();
        const callingCode = getCountryCallingCode(countryCode);
        return (
          label.includes(normalizedCountrySearch) ||
          displayCountryCode(countryCode).toLowerCase().includes(normalizedCountrySearch) ||
          callingCode.includes(normalizedCountrySearch)
        );
      })
    : COUNTRIES;

  return (
    <AuthMarketingLeftColumn className="flex min-h-0 flex-1 w-full items-center justify-center px-4 py-[38px]">
      <div className="w-full max-w-[400px]">
        <div className="min-h-[706px] rounded-lg bg-paper-light px-7 pb-[30px] pt-8 shadow-none sm:px-[39px]">
          <a
            href="https://www.charitystack.com/"
            aria-label="CharityStack Dashboard"
            className="mx-auto block w-fit rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-sage"
          >
            <img src={charityStackLogo} alt="CharityStack Logo" className="w-24 h-auto sm:w-32" />
          </a>
          <h1 className="mt-7 text-center text-2xl font-semibold text-ink">
            {verificationChannel ? 'Verify your account' : 'Create your account'}
          </h1>
          <p className="mt-2 text-center text-sm text-ink-secondary">
            {verificationChannel
              ? `Enter the code sent to your ${verificationChannel === 'phone' ? 'phone' : 'email address'}.`
              : 'Welcome! Please fill in the details to get started.'}
          </p>

          {verificationChannel ? (
            <form className="mt-9 space-y-6" onSubmit={handleVerification}>
              <label className="block text-sm font-medium text-ink" htmlFor="verificationCode">
                Verification code
                <input
                  id="verificationCode"
                  name="verificationCode"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={verificationCode}
                  onChange={(event) => setVerificationCode(event.target.value)}
                  className={fieldClass('verificationCode')}
                  required
                />
              </label>
              {formError ? <p className="text-sm text-status-red-text">{formError}</p> : null}
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex h-10 w-full items-center justify-center rounded-md border border-sage bg-sage text-sm font-medium text-white hover:bg-sage-hover disabled:opacity-60"
              >
                {isSubmitting ? 'Verifying…' : 'Verify and continue'}
              </button>
            </form>
          ) : (
            <form className="mt-9 space-y-6" onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm font-medium text-ink" htmlFor="firstName">
                  First name
                  <input
                    id="firstName"
                    name="firstName"
                    value={form.firstName}
                    onChange={updateForm}
                    className={fieldClass('firstName')}
                    autoComplete="given-name"
                    placeholder="First name"
                  />
                  {fieldErrors.firstName ? (
                    <span className="mt-1 block text-xs text-status-red-text">
                      {fieldErrors.firstName}
                    </span>
                  ) : null}
                </label>
                <label className="block text-sm font-medium text-ink" htmlFor="lastName">
                  Last name
                  <input
                    id="lastName"
                    name="lastName"
                    value={form.lastName}
                    onChange={updateForm}
                    className={fieldClass('lastName')}
                    autoComplete="family-name"
                    placeholder="Last name"
                  />
                  {fieldErrors.lastName ? (
                    <span className="mt-1 block text-xs text-status-red-text">
                      {fieldErrors.lastName}
                    </span>
                  ) : null}
                </label>
              </div>
              <label className="block text-sm font-medium text-ink" htmlFor="emailAddress">
                Email address
                <input
                  id="emailAddress"
                  type="email"
                  name="emailAddress"
                  value={form.emailAddress}
                  onChange={updateForm}
                  className={fieldClass('emailAddress')}
                  autoComplete="email"
                  placeholder="Enter your email address"
                />
                {fieldErrors.emailAddress ? (
                  <span className="mt-1 block text-xs text-status-red-text">
                    {fieldErrors.emailAddress}
                  </span>
                ) : null}
              </label>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-ink" htmlFor="phoneNumber">
                    Phone number
                  </label>
                  <span className="text-xs font-normal text-ink-secondary">Optional</span>
                </div>
                <div
                  ref={countryPickerRef}
                  className={`relative mt-2 flex h-10 w-full rounded-md border bg-white text-sm text-ink ring-offset-white focus-within:ring-2 focus-within:ring-sage focus-within:ring-offset-2 ${
                    fieldErrors.phoneNumber ? 'border-status-red-text' : 'border-edge-input'
                  }`}
                >
                  <button
                    type="button"
                    aria-label="Country"
                    aria-expanded={countryPickerOpen}
                    aria-haspopup="listbox"
                    className="flex h-full w-[60px] shrink-0 items-center justify-between border-0 border-r border-edge-input bg-white px-2 text-sm text-ink outline-none"
                    onClick={() => setCountryPickerOpen((open) => !open)}
                  >
                    <span>{displayCountryCode(country)}</span>
                    <ChevronDown className="h-3.5 w-3.5 text-ink-muted" aria-hidden="true" />
                  </button>
                  <span className="flex h-full items-center pl-2 text-ink">
                    +{getCountryCallingCode(country)}
                  </span>
                  <input
                    id="phoneNumber"
                    type="tel"
                    name="phoneNumber"
                    value={form.phoneNumber}
                    onChange={updatePhoneNumber}
                    className="h-full min-w-0 flex-1 border-0 bg-transparent px-2 text-sm text-ink outline-none placeholder:text-ink-muted"
                    autoComplete="tel-national"
                    placeholder="Enter your phone number"
                  />
                  {countryPickerOpen ? (
                    <div className="absolute left-0 top-full z-50 mt-1 w-full rounded-md border border-edge-input bg-white p-2 shadow-lg">
                      <input
                        type="search"
                        aria-label="Search country or code"
                        value={countrySearch}
                        onChange={(event) => setCountrySearch(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Escape') {
                            setCountrySearch('');
                            setCountryPickerOpen(false);
                          }
                        }}
                        autoFocus
                        className="h-9 w-full rounded-md border border-edge-input bg-white px-3 text-sm text-ink outline-none placeholder:text-ink-muted focus:ring-2 focus:ring-sage"
                        placeholder="Search country or code"
                      />
                      <div
                        role="listbox"
                        aria-label="Countries"
                        className="mt-2 max-h-64 overflow-y-auto"
                      >
                        {filteredCountries.map((countryCode) => (
                          <button
                            key={countryCode}
                            type="button"
                            role="option"
                            aria-selected={countryCode === country}
                            className="flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm text-ink hover:bg-paper-muted focus:bg-paper-muted focus:outline-none"
                            onClick={() => selectCountry(countryCode)}
                          >
                            <span>{COUNTRY_LABELS[countryCode]}</span>
                            <span className="text-ink-secondary">
                              +{getCountryCallingCode(countryCode)}
                            </span>
                          </button>
                        ))}
                        {filteredCountries.length === 0 ? (
                          <p className="px-2 py-3 text-sm text-ink-secondary">
                            No countries found.
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
                {fieldErrors.phoneNumber ? (
                  <span className="mt-1 block text-xs text-status-red-text">
                    {fieldErrors.phoneNumber}
                  </span>
                ) : null}
              </div>
              <div>
                <label className="block text-sm font-medium text-ink" htmlFor="password">
                  Password
                </label>
                <span className="relative block">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={form.password}
                    onChange={updateForm}
                    className={`${fieldClass('password')} pr-10`}
                    autoComplete="new-password"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-sage"
                    onClick={() => setShowPassword((current) => !current)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </span>
                {fieldErrors.password ? (
                  <span className="mt-2 block text-xs text-status-red-text">
                    {fieldErrors.password}
                  </span>
                ) : passwordFeedback ? (
                  <span
                    className={`mt-2 flex items-start gap-1.5 text-sm leading-[18px] ${
                      passwordFeedback.type === 'error'
                        ? 'text-status-red-text'
                        : 'text-ink-secondary'
                    }`}
                  >
                    {passwordFeedback.type === 'success' ? (
                      <span className="mt-0.5 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-sage text-[10px] font-semibold leading-none text-white">
                        ✓
                      </span>
                    ) : null}
                    <span>{passwordFeedback.message}</span>
                  </span>
                ) : null}
              </div>
              <div className="flex items-start gap-1.5 text-sm leading-[18px] text-ink">
                <Checkbox
                  checked={form.legalAccepted}
                  onChange={(checked) => {
                    setForm((current) => ({ ...current, legalAccepted: checked }));
                    setFieldErrors((current) => ({ ...current, legalAccepted: '' }));
                    setFormError('');
                  }}
                  ariaLabel="I agree to the Terms of Service and Privacy Policy"
                  className="mt-0.5 shrink-0 [&>button]:h-4 [&>button]:w-4"
                />
                <span className="pr-4">
                  I agree to the{' '}
                  <a
                    className="text-sage underline underline-offset-2"
                    href="https://www.charitystack.com/terms-of-service"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    className="text-sage underline underline-offset-2"
                    href="https://www.charitystack.com/privacy"
                  >
                    Privacy Policy
                  </a>
                </span>
              </div>
              {fieldErrors.legalAccepted ? (
                <p className="-mt-6 text-xs text-status-red-text">{fieldErrors.legalAccepted}</p>
              ) : null}
              {formError ? <p className="-mt-6 text-sm text-status-red-text">{formError}</p> : null}
              <div
                id="clerk-captcha"
                data-cl-theme="auto"
                data-cl-size="normal"
                data-cl-language="en-us"
                className="flex justify-center empty:hidden"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex h-10 w-full items-center justify-center rounded-md border border-sage bg-sage text-sm font-medium text-white hover:bg-sage-hover disabled:opacity-60"
              >
                {isSubmitting ? (
                  'Creating account…'
                ) : (
                  <>
                    Continue{' '}
                    <span className="ml-1 text-[10px]" aria-hidden="true">
                      ▸
                    </span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
        {!verificationChannel ? (
          <p className="mt-2 flex h-[69px] items-center justify-center rounded-lg bg-paper-muted text-center text-sm text-ink-secondary">
            Already have an account?{' '}
            <a className="ml-1 text-sage hover:underline" href={ROUTER_BASENAME + 'signin'}>
              Sign in
            </a>
          </p>
        ) : null}
      </div>
    </AuthMarketingLeftColumn>
  );
}

export default SignUpContent;
